<template>
  <div class="app-layout-root">
    <el-container class="app-layout">
      <el-aside v-if="!isMobile" width="220px" class="app-sidebar">
        <div class="sidebar-logo">
          <span>{{ companyShortName }}</span>
        </div>
        <el-menu
          :default-active="activeMenu"
          router
          class="sidebar-menu"
        >
          <el-menu-item index="/custom">
            <el-icon><Document /></el-icon>
            <span>学习任务</span>
          </el-menu-item>
          <el-menu-item index="/material">
            <el-icon><Folder /></el-icon>
            <span>学习资料</span>
          </el-menu-item>
          <el-menu-item index="/course">
            <el-icon><Reading /></el-icon>
            <span>公开课</span>
          </el-menu-item>
          <el-menu-item index="/profile">
            <el-icon><User /></el-icon>
            <span>个人中心</span>
          </el-menu-item>
        </el-menu>
      </el-aside>

      <el-container class="app-body">
        <el-header class="app-header">
          <div class="header-title">{{ pageTitle }}</div>
          <el-dropdown trigger="click" @command="handleCommand">
            <div class="user-info">
              <UserAvatar :size="32" :name="accountInfo?.realName" :headimg="accountInfo?.headimg" />
              <span class="user-name">{{ accountInfo?.realName || '学员' }}</span>
              <el-icon class="user-arrow"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                <el-dropdown-item command="setting">设置</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </el-header>
        <el-main class="app-main" :class="{ 'is-play-page': isPlayPage }">
          <router-view />
        </el-main>
      </el-container>
    </el-container>

    <MobileBottomNav />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Document, User, ArrowDown, Reading, Folder } from '@element-plus/icons-vue'
import { useGlobalStore } from '@/store/global'
import UserAvatar from '@/components/common/UserAvatar.vue'
import MobileBottomNav from '@/components/common/MobileBottomNav.vue'
import Auth from '@/utils/auth'
import Util from '@/utils/util'
import { useIsMobile } from '@/utils/responsive'

const route = useRoute()
const router = useRouter()
const isMobile = useIsMobile()

const accountInfo = computed(() => useGlobalStore.state.accountInfo)
const companyStyle = computed(() => useGlobalStore.state.companyStyle)

const activeMenu = computed(() => {
  if (route.path.startsWith('/profile')) return '/profile'
  if (route.path.startsWith('/material')) return '/material'
  if (route.path.startsWith('/course')) return '/course'
  if (route.path.startsWith('/play')) {
    if (route.query.from === 'course') return '/course'
    if (route.query.from === 'material') return '/material'
    return '/custom'
  }
  if (route.path.startsWith('/custom')) return '/custom'
  return '/custom'
})

const pageTitle = computed(() => route.meta.title || '学习中心')
const isPlayPage = computed(() => route.path.startsWith('/play'))

const companyShortName = computed(() => {
  return companyStyle.value?.shortName
    || companyStyle.value?.companyName
    || '学习中心'
})

function handleCommand(cmd) {
  if (cmd === 'profile') router.push('/profile')
  else if (cmd === 'setting') router.push('/profile/setting')
  else if (cmd === 'logout') {
    Util.confirm('确定退出登录吗？').then((ok) => {
      if (ok) Auth.exit()
    })
  }
}
</script>

<style scoped>
.app-layout-root {
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
}

.app-layout {
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
}

.app-layout :deep(.el-container),
.app-layout :deep(.el-main),
.app-layout :deep(.el-header) {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.app-body {
  flex: 1;
  width: 100%;
  min-width: 0;
}

.app-sidebar {
  background: var(--member-sidebar-bg);
  border-right: 1px solid #ebebeb;
  display: flex;
  flex-direction: column;
}

.sidebar-logo {
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  font-weight: 600;
  font-size: 15px;
  color: var(--member-text-primary);
  border-bottom: 1px solid #ebebeb;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-menu {
  border-right: none;
  background: transparent;
  padding: 12px 10px;
  flex: 1;
}

.sidebar-menu :deep(.el-menu-item) {
  border-radius: 8px;
  margin-bottom: 4px;
  height: 44px;
  color: var(--member-text-primary);
}

.sidebar-menu :deep(.el-menu-item:hover) {
  background: rgba(0, 0, 0, 0.04);
}

.sidebar-menu :deep(.el-menu-item.is-active) {
  background: var(--member-sidebar-active-bg);
  color: var(--member-text-primary);
  font-weight: 500;
}

.sidebar-menu :deep(.el-menu-item.is-active .el-icon) {
  color: var(--member-text-primary);
}

.app-header {
  background: var(--member-page-bg);
  border-bottom: 1px solid #ebebeb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 56px;
  width: 100%;
  box-sizing: border-box;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--member-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #333;
  flex-shrink: 0;
}

.user-name {
  font-size: 14px;
}

.app-main {
  background: var(--member-page-bg);
  padding: 20px 24px;
  padding-bottom: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

@media (max-width: 768px) {
  .app-header {
    padding: 0 12px;
  }

  .user-name,
  .user-arrow {
    display: none;
  }

  .app-main {
    padding: 12px;
    padding-bottom: calc(var(--member-bottom-nav-height) + env(safe-area-inset-bottom, 0) + 12px);
  }

  .app-main.is-play-page {
    padding-bottom: 12px;
  }
}
</style>
